// E15 Home: what the engine town shows. The town only ever shows something real, as one of three signals:
//   - needs you (an action figure): a decision or approval waiting for the owner;
//   - new message (a message sign): a reply, enquiry or handed-over conversation to read;
//   - needs fixing (gears): a part of the engine that has stopped working, or an account that has.
// Each signal sits on the building, the office inside it and the desk where it is dealt with, so it can be followed
// down in three clicks. A module that is not switched on, or has no accounts connected yet, is drawn greyed out with an
// Activate button that opens its Accounts. Nothing else on the town moves or pops up.
import type { HumanTask, JsonObject } from "./types";

/** A task as the town needs it: its kind, and its details when there are any (to place a part that is failing). */
export type TownTask = Pick<HumanTask, "id" | "kind"> & { payload?: JsonObject | null; entity_type?: string | null };
/** A to-do or next step as the town needs it: its area and whether it is due yet. */
export type TownToDo = Pick<OwnerTask, "bucket" | "source" | "person_id" | "organisation_id" | "deal_id">;
import type { StepLive } from "./process";
import type { ModuleKey } from "./setupCatalogue";
import type { ModuleView } from "./setupFlow";
import { TASK_HOMES } from "./nav";
import { deskIndex, type BuildingId, type EngineTownData } from "./engineTown";
import { areaOf, areaOfOwnerTask, isDue, isNextStep, type AreaKey } from "./inbox";
import type { OwnerTask } from "./ownerTasks";

export type TownBuildingId = BuildingId | "hq" | "complete" | "serve";
export type SignalKind = "needs" | "message" | "fix";

export interface Signal {
  needs: number;
  message: number;
  fix: number;
}

/** A module's state in the town: running, not switched on, switched on with nothing connected, or not built yet. */
export type BuildingStatus = "on" | "off" | "unset" | "soon";

export interface TownSignals {
  buildings: Partial<Record<TownBuildingId, Signal>>;
  offices: Record<string, Signal>;
  desks: Record<string, Signal>;
  status: Record<TownBuildingId, BuildingStatus>;
  /** Where Activate goes for a greyed-out module: its Accounts. */
  activate: Partial<Record<TownBuildingId, string>>;
}

/** Tasks that are someone getting in touch: shown as a message sign, not an action figure. */
export const MESSAGE_KINDS = new Set(["decide_reply", "review_reply", "whatsapp_handoff", "missed_call"]);

/** Tasks that mean something has stopped working: shown as gears. */
export const FIX_KINDS = new Set([
  "health_alert",
  "engine_part_failing",
  "budget_reached",
  "review_quality_failure",
  "booking_problem",
  "whatsapp_template_problem",
  "notetaker_problem",
  "payment_problem",
  "reconnect_phone_line",
  "contact_finder_problem",
]);

export function signalOf(kind: string): SignalKind {
  if (FIX_KINDS.has(kind)) return "fix";
  if (MESSAGE_KINDS.has(kind)) return "message";
  return "needs";
}

/** Which building and office each part of the engine works in (agents are named in engine/schedules.py). */
export const AGENT_HOME: Record<string, { building: TownBuildingId; office: string }> = {
  "strategy-agent": { building: "hq", office: "" },
  "ops-agent": { building: "hq", office: "" },
  "content-agent": { building: "attract", office: "actions" },
  "social-agent": { building: "attract", office: "actions" },
  "listings-agent": { building: "network", office: "actions" },
  "sourcing-agent": { building: "find", office: "research" },
  "enrichment-agent": { building: "find", office: "research" },
  "outreach-agent": { building: "find", office: "actions" },
  "inbox-agent": { building: "connect", office: "actions" },
  "scheduler-agent": { building: "connect", office: "actions" },
  "deal-agent": { building: "connect", office: "actions" },
  "relationship-agent": { building: "reconnect", office: "actions" },
};

/** An error that means an account needs signing in again (or connecting), so the fix is in Accounts. */
export function isAccountProblem(text: string): boolean {
  return /sign(ed)?[ -]?in|signing in|expired|reconnect|re-?authori[sz]e|token|credentials|not connected|password/i.test(text);
}

const PLAIN_OFFICES = ["accounts", "rules", "research", "actions", "performance"];

/** Where a task's signal sits: desk (page address), office and building. Null for one that belongs nowhere in the town. */
export function placeTask(
  t: Pick<TownTask, "kind" | "payload">,
  data: EngineTownData,
  index = deskIndex(data),
): { building: TownBuildingId; office: string | null; desk: string | null } | null {
  const payload = (t.payload ?? {}) as Record<string, unknown>;
  // A part of the engine failing: on the building and office it works in (the Accounts office if it is an account).
  if (t.kind === "health_alert" || t.kind === "engine_part_failing") {
    const home = AGENT_HOME[String(payload.agent ?? "")];
    if (home) {
      const text = `${String(payload.error ?? "")} ${String(payload.note ?? "")} ${String(payload.problem ?? "")}`;
      const office = home.office ? `${home.building}:${isAccountProblem(text) ? "accounts" : home.office}` : null;
      return { building: home.building, office: office && data.rooms[office] ? office : null, desk: null };
    }
  }
  for (const to of TASK_HOMES[t.kind] ?? []) {
    const at = index.get(to);
    if (at) return { building: at.building, office: at.office, desk: to };
  }
  const first = (TASK_HOMES[t.kind] ?? [])[0];
  // Settings's pages and Settings belong to the HQ building: the playbook, approvals, spending limits and the engine's schedule.
  if (first && (first.startsWith("/settings/business") || first.startsWith("/settings"))) return { building: "hq", office: null, desk: null };
  return null;
}

const ZERO = (): Signal => ({ needs: 0, message: 0, fix: 0 });

/** The building that shows each inbox panel's count: a module's own, CRM's, and Settings for General. */
const AREA_BUILDING: Record<AreaKey, TownBuildingId> = {
  attract: "attract",
  find: "find",
  network: "network",
  connect: "connect",
  reconnect: "reconnect",
  crm: "crm",
  general: "hq",
};

export interface SignalInputs {
  tasks: TownTask[] | null;
  /** The signed-in member's to-dos and next steps (crm_owner_tasks); those due today or earlier count as needs you. */
  todos?: TownToDo[] | null;
  live?: Map<string, StepLive> | null;
  /** The owner's module choices (crm.setupState().modules); null while loading or if the read fails. */
  modules?: Partial<Record<ModuleKey, Pick<ModuleView, "state">>> | null;
}

/**
 * Every signal in the town, and which buildings are greyed out. A building's figures are its inbox panel's count
 * (lib/inbox.ts areaOf, with the member's due to-dos), so the town and Home agree; offices and desks keep
 * placeTask's finer placement.
 */
export function townSignals({ tasks, todos, live, modules }: SignalInputs, data: EngineTownData): TownSignals {
  const index = deskIndex(data);
  const out: TownSignals = { buildings: {}, offices: {}, desks: {}, status: {} as Record<TownBuildingId, BuildingStatus>, activate: {} };
  const bump = (kind: SignalKind, at: { building?: TownBuildingId; office?: string | null; desk?: string | null }) => {
    if (at.building) (out.buildings[at.building] ??= ZERO())[kind] += 1;
    if (at.office) (out.offices[at.office] ??= ZERO())[kind] += 1;
    if (at.desk) (out.desks[at.desk] ??= ZERO())[kind] += 1;
  };
  for (const t of tasks ?? []) {
    const at = placeTask(t, data, index);
    if (at) bump(signalOf(t.kind), { office: at.office, desk: at.desk });
    // A next step is counted with the to-dos below, as on its panel.
    if (!isNextStep({ kind: t.kind, entity_type: t.entity_type ?? null, payload: t.payload ?? {} })) {
      bump(signalOf(t.kind), { building: AREA_BUILDING[areaOf({ kind: t.kind, payload: t.payload ?? {} })] });
    }
  }
  for (const t of todos ?? []) if (isDue(t)) bump("needs", { building: AREA_BUILDING[areaOfOwnerTask(t)] });
  for (const b of data.buildings) {
    const id = b.id as TownBuildingId;
    if (b.kind === "soon") {
      out.status[id] = "soon";
      continue;
    }
    if (b.kind !== "module") {
      out.status[id] = "on";
      continue;
    }
    out.status[id] = moduleStatus(id as BuildingId, data, live, modules);
    const accounts = data.rooms[`${id}:accounts`]?.desks[0]?.to;
    if (out.status[id] !== "on" && accounts) out.activate[id] = accounts;
  }
  return out;
}

/** Which of a module's accounts are missing: the outside accounts its steps need that are not connected. */
export function missingAccounts(b: BuildingId, data: EngineTownData, live?: Map<string, StepLive> | null): { connected: number; missing: string[]; events: number } {
  const missing = new Set<string>();
  const connected = new Set<string>();
  let events = 0;
  if (!live) return { connected: 0, missing: [], events: 0 };
  for (const office of PLAIN_OFFICES) {
    for (const d of data.rooms[`${b}:${office}`]?.desks ?? []) {
      const s = d.step ? live.get(d.step) : undefined;
      if (!s || s.applies === false) continue;
      events += (s.events_30d ?? 0) + (s.open_tasks ?? 0);
      for (const v of s.vendors ?? []) {
        if (v.connected) connected.add(v.key);
        else if (v.required) missing.add(v.name || v.key);
      }
    }
  }
  return { connected: connected.size, missing: [...missing].filter((m) => !connected.has(m)), events };
}

/**
 * A module is greyed out when the owner has not switched it on, or when it is on but none of the accounts it needs is
 * connected yet and it has done nothing in 30 days (so nothing can run). Connect is always on (every reply comes
 * through it).
 */
export function moduleStatus(
  b: BuildingId,
  data: EngineTownData,
  live?: Map<string, StepLive> | null,
  modules?: SignalInputs["modules"],
): BuildingStatus {
  if (b === "crm" || b === "connect") return "on";
  const choice = modules?.[b as ModuleKey]?.state;
  if (choice && choice !== "on") return "off";
  const acc = missingAccounts(b, data, live);
  // Nothing connected and nothing done in the last 30 days: it cannot run yet.
  if (acc.missing.length && acc.connected === 0 && acc.events === 0) return "unset";
  return "on";
}

/** "2 things need you", for a screen reader and the plate's title. */
export function signalWords(s: Signal | undefined): string {
  if (!s) return "";
  const parts: string[] = [];
  if (s.fix) parts.push(`${s.fix} ${s.fix === 1 ? "thing needs" : "things need"} fixing`);
  if (s.message) parts.push(`${s.message} new ${s.message === 1 ? "message" : "messages"}`);
  if (s.needs) parts.push(`${s.needs} waiting for you`);
  return parts.join(", ");
}
